import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import ConsultationFooter from "@/components/ConsultationFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1925 }, (_, i) => currentYear - i);

function ageFromDob(y, m, d) {
  if (!y || m === "" || m === undefined || m === null || !d) return null;
  const today = new Date();
  const dob = new Date(Number(y), Number(m), Number(d));
  if (isNaN(dob.getTime())) return null;
  let a = today.getFullYear() - dob.getFullYear();
  const md = (today.getMonth() < dob.getMonth()) || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (md) a--;
  return a;
}

export default function PublicRiskAssessment() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, status: null, data: null });
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [occupation, setOccupation] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const age = useMemo(() => ageFromDob(dobYear, dobMonth, dobDay), [dobYear, dobMonth, dobDay]);

  useEffect(() => {
    document.title = "Risk Profile Questionnaire · Reliserve Wealth";
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    axios.get(`${API}/public/risk-assessment/${token}`)
      .then(r => setState({ loading: false, status: r.data.status, data: r.data }))
      .catch(e => setState({ loading: false, status: e.response?.status === 404 ? "NotFound" : "Error", data: null }));
    return () => { try { document.head.removeChild(meta); } catch {} };
  }, [token]);

  const submit = async () => {
    setErrorMsg("");
    if (!dobDay || dobMonth === "" || !dobYear) return setErrorMsg("Please provide your complete date of birth.");
    if (!occupation) return setErrorMsg("Please select your occupation.");
    if (age !== null && age < 18) return setErrorMsg("You must be at least 18 to complete this assessment.");
    const missing = (state.data?.questions || []).filter(q => !answers[q.key]);
    if (missing.length > 0) return setErrorMsg("Please answer every question before generating your profile.");
    setBusy(true);
    try {
      const dob = `${dobYear}-${String(Number(dobMonth) + 1).padStart(2, "0")}-${String(dobDay).padStart(2, "0")}`;
      const { data } = await axios.post(`${API}/public/risk-assessment/${token}/submit`, { dob, occupation, responses: answers });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong — please try again.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const printCard = () => window.print();

  if (state.loading) return <PageShell><div className="py-24 text-center text-muted-foreground">Loading…</div></PageShell>;

  if (state.status === "Completed") return <PageShell><InfoCard title="Assessment already submitted" body="You've already completed this assessment. A copy of your Risk Profile was emailed to you." /></PageShell>;
  if (state.status === "Cancelled") return <PageShell><InfoCard title="This link is no longer active" body="For a fresh assessment, please write to contact@reliservewealth.com." /></PageShell>;
  if (state.status === "Expired") return <PageShell><InfoCard title="This link has expired" body="Please reach out to contact@reliservewealth.com to request a fresh assessment." /></PageShell>;
  if (state.status === "NotFound" || state.status === "Error") return <PageShell><InfoCard title="Link not found" body="Please double-check the URL or write to contact@reliservewealth.com." /></PageShell>;

  if (result) return <PageShell><ResultCard result={result} onPrint={printCard} /></PageShell>;

  const clientName = state.data?.client_name || "";
  const questions = state.data?.questions || [];
  const q1to8 = questions.filter(q => q.counts_in_score);
  const q9to10 = questions.filter(q => !q.counts_in_score);

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <h1 className="font-dashboard text-3xl font-bold tracking-tight md:text-4xl">Investor Risk Profile &amp; Portfolio Assessment</h1>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">Answer a few honest questions about how you think about money, markets and risk. There are no right or wrong answers — each one simply helps us understand you better, so the plan we build together actually fits how you'd react in real situations.</p>
        </div>

        <section className="mb-8 rounded-xl border border-black/10 bg-white p-6 shadow-sm">
          <p className="overline mb-1 text-muted-foreground">Step 1</p>
          <h2 className="font-dashboard text-xl font-bold">Your Details</h2>

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Full Name</label>
              <div className="mt-1 rounded-md border border-black/10 bg-muted/40 px-3 py-2 text-sm" data-testid="locked-name">{clientName}</div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Date of Birth</label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                <select className="rounded-md border border-black/10 px-3 py-2 text-sm" value={dobDay} onChange={e => setDobDay(e.target.value)} data-testid="dob-day">
                  <option value="">Day</option>{DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="rounded-md border border-black/10 px-3 py-2 text-sm" value={dobMonth} onChange={e => setDobMonth(e.target.value)} data-testid="dob-month">
                  <option value="">Month</option>{MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select className="rounded-md border border-black/10 px-3 py-2 text-sm" value={dobYear} onChange={e => setDobYear(e.target.value)} data-testid="dob-year">
                  <option value="">Year</option>{YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {age !== null && <div className="mt-2 inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary" data-testid="age-chip">Age: {age} years</div>}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Occupation</label>
              <select className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm" value={occupation} onChange={e => setOccupation(e.target.value)} data-testid="occupation">
                <option value="">Select occupation</option>
                {(state.data?.occupations || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-black/10 bg-white p-6 shadow-sm">
          <p className="overline mb-1 text-muted-foreground">Step 2</p>
          <h2 className="font-dashboard text-xl font-bold">Understanding Your Investing Mindset</h2>
          <p className="mt-2 text-sm text-muted-foreground">Each question includes a hypothetical scenario — read it before choosing the option closest to how you'd genuinely react.</p>
          {q1to8.map((q, i) => <QuestionCard key={q.key} q={q} index={i + 1} total={q1to8.length} value={answers[q.key]} onChange={v => setAnswers(a => ({ ...a, [q.key]: v }))} />)}
        </section>

        <section className="mb-8 rounded-xl border border-black/10 bg-white p-6 shadow-sm">
          <p className="overline mb-1 text-muted-foreground">Step 3</p>
          <h2 className="font-dashboard text-xl font-bold">Protection Check</h2>
          <p className="mt-2 text-sm text-muted-foreground flex items-start gap-2">
            <span>🛡</span>
            <span>Insurance protects your family from the unexpected — it works alongside your investments, not as part of them. These two questions are assessed separately.</span>
          </p>
          {q9to10.map((q, i) => <QuestionCard key={q.key} q={q} index={i + 1} total={q9to10.length} value={answers[q.key]} onChange={v => setAnswers(a => ({ ...a, [q.key]: v }))} />)}
        </section>

        {errorMsg && <p className="mb-4 text-sm text-red-600" data-testid="err-msg">{errorMsg}</p>}
        <div className="mb-16 flex justify-end">
          <button onClick={submit} disabled={busy} className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="submit-btn">
            {busy ? "Generating…" : "Generate My Risk Profile"}
          </button>
        </div>
      </div>
    </PageShell>
  );
}

function QuestionCard({ q, index, total, value, onChange }) {
  return (
    <fieldset className="mt-6 rounded-lg border border-black/10 p-5" data-testid={`q-card-${q.key}`}>
      <legend className="px-2">
        <p className="overline text-muted-foreground">Question {index} of {total}</p>
        <p className="mt-1 text-base font-semibold text-foreground">{q.title}</p>
      </legend>
      <div className="my-3 flex items-start gap-3 rounded-md bg-accent/10 p-3 text-sm">
        <span>💡</span>
        <div><span className="text-[10px] font-bold uppercase tracking-wider text-accent">Picture this  </span>{q.scenario}</div>
      </div>
      <div className="space-y-2">
        {q.options.map(o => (
          <label key={o.value} className={"flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm " + (value === o.value ? "border-primary bg-primary/5" : "border-black/10 hover:bg-muted/40")}>
            <input type="radio" name={q.key} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} data-testid={`${q.key}-opt-${o.value}`} className="accent-primary" />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F6F4F1] to-white">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-sm print:hidden">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-4"><BrandLogo size="sm" /></div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">{children}</main>
      <div className="print:hidden"><ConsultationFooter /></div>
    </div>
  );
}

function InfoCard({ title, body }) {
  return (
    <div className="mx-auto mt-20 max-w-lg rounded-xl border border-black/10 bg-white p-8 text-center shadow-sm">
      <h2 className="font-dashboard text-xl font-bold">{title}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function ResultCard({ result, onPrint }) {
  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-xl border border-primary/30 bg-white p-8 shadow-sm print:shadow-none print:border-black/20" data-testid="result-card">
        <p className="overline mb-2 text-primary">Your Risk Profile</p>
        <h2 className="font-dashboard text-3xl font-bold tracking-tight" data-testid="result-profile">{result.risk_profile} Investor</h2>
        <p className="mt-4 text-sm leading-relaxed text-foreground/80">{result.profile_description}</p>

        <div className="mt-8 grid grid-cols-2 gap-4 border-t border-black/10 pt-6 text-sm">
          <div><p className="text-xs text-muted-foreground">Name</p><p className="font-medium">{result.client_name}</p></div>
          <div><p className="text-xs text-muted-foreground">Date of Birth</p><p className="font-medium">{result.dob}</p></div>
          <div><p className="text-xs text-muted-foreground">Age</p><p className="font-medium">{result.age_at_assessment} years</p></div>
          <div><p className="text-xs text-muted-foreground">Occupation</p><p className="font-medium">{result.occupation}</p></div>
          <div><p className="text-xs text-muted-foreground">Assessment Score</p><p className="font-medium">{result.assessment_score} / 40</p></div>
          <div><p className="text-xs text-muted-foreground">Date of Assessment</p><p className="font-medium">{(result.completed_at || "").slice(0, 10)}</p></div>
        </div>

        <p className="mt-6 text-[11px] leading-relaxed text-muted-foreground border-t border-black/10 pt-4">
          Reliserve Wealth is the brand under which Rohan Ghotage operates as an AMFI-registered Mutual Fund Distributor (ARN: 369518) and an IRDAI-registered Insurance Agent. Mutual Fund investments are subject to market risks; please read all scheme-related documents carefully. Insurance is a subject matter of solicitation. Past performance is not indicative of future returns. This assessment is a written record of your responses and is not, by itself, investment advice.
        </p>
      </div>
      <div className="mt-6 flex justify-end print:hidden">
        <button onClick={onPrint} className="rounded-full border border-primary/40 px-6 py-2 text-sm text-primary hover:bg-primary/5" data-testid="print-btn">Print / Save as PDF</button>
      </div>
    </div>
  );
}
